import { Button } from '../../../shared/ui'
import { useElapsedSince } from '../../../shared/hooks/useElapsedSince'
import { formatDurationDe } from '../../../shared/lib/durationDe'
import type { PrimaryWorkAction } from '../model/workActionPolicy'

const LABELS: Record<PrimaryWorkAction, string> = {
  'start-travel': 'Anfahrt starten',
  start: 'Arbeit starten',
  complete: 'Arbeit abschließen',
  'end-departure': 'Abfahrt beenden',
}

/**
 * Shortened labels for `compact` — the mobile bottom bar, which puts this button in a half-width
 * row next to a secondary action. Only `complete` needs one: it is the one action ever offered
 * alongside both a secondary button and (previously) an elapsed chip, and "Arbeit abschließen" at
 * full size wrapped onto two lines there.
 */
const COMPACT_LABELS: Partial<Record<PrimaryWorkAction, string>> = {
  complete: 'Abschließen',
}

interface Props {
  action: PrimaryWorkAction
  onAction: (action: PrimaryWorkAction) => void
  /**
   * ISO anchor of the RUNNING phase — shows a live elapsed chip next to the label:
   * `travelStartedAt` while the Anfahrt runs, `actualWorkStartsAt` while working,
   * `actualEndsAt` while the Abfahrt runs. Omit for the fresh-start actions.
   *
   * Ignored when `compact` is set — see the note there.
   */
  elapsedSince?: string | null
  /** solid = appointment style (green fill), outline = task style (green border). */
  appearance?: 'solid' | 'outline'
  isPending?: boolean
  className?: string
  /**
   * The mobile bottom bar: smaller type, a shortened label, and no elapsed chip. The chip is
   * dropped rather than shrunk further — `Zeiten` on the page body already shows the same live
   * duration prominently, so repeating it here was both what overflowed the button and duplicate
   * information once `Zeiten` stopped hiding behind a collapsed disclosure.
   */
  compact?: boolean
}

/**
 * The green phase button both worker pages share. Which action it offers comes from
 * getPrimaryWorkAction — render nothing when that returns null.
 */
export function PrimaryWorkButton({
  action,
  onAction,
  elapsedSince,
  appearance = 'solid',
  isPending = false,
  className,
  compact = false,
}: Props) {
  const ageMs = useElapsedSince(!compact && elapsedSince ? new Date(elapsedSince).getTime() : null)
  const styles =
    appearance === 'solid'
      ? '!bg-green-500 !text-white leading-5'
      : 'border-2 border-green-500 !text-green-500'

  return (
    <Button
      variant={appearance === 'outline' ? 'surface' : undefined}
      size={compact ? 'sm' : undefined}
      className={[styles, className].filter(Boolean).join(' ')}
      onClick={() => onAction(action)}
      disabled={isPending}
    >
      {compact ? (COMPACT_LABELS[action] ?? LABELS[action]) : LABELS[action]}
      {ageMs !== null && (
        // aria-hidden keeps the accessible name stable while the label ticks.
        <span aria-hidden="true" className="ml-2 text-sm font-normal opacity-80 tabular-nums">
          {formatDurationDe(Math.floor(ageMs / 60_000))}
        </span>
      )}
    </Button>
  )
}
